import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './Admin.css';

export default function Admin() {
const { user } = useAuth();
const navigate = useNavigate();
const [tab, setTab] = useState('overview');
const [stats, setStats] = useState(null);
const [users, setUsers] = useState([]);
const [invitations, setInvitations] = useState([]);
const [navItems, setNavItems] = useState([]);
const [inviteForm, setInviteForm] = useState({ email: '', phone: '', name: '' });
const [navForm, setNavForm] = useState({ parent: 'recipes', label: '', search_value: '' });
const [message, setMessage] = useState('');
const [error, setError] = useState('');

useEffect(() => {
if (user?.role !== 'admin') { navigate('/'); return; }
loadData();
}, [user]);

const loadData = async () => {
try {
const [statsRes, usersRes, invRes, navRes] = await Promise.all([
api.get('/admin/stats'),
api.get('/admin/users'),
api.get('/admin/invitations'),
api.get('/admin/nav'),
]);
setStats(statsRes.data.stats);
setUsers(usersRes.data.users);
setInvitations(invRes.data.invitations);
setNavItems(navRes.data.nav_items);
} catch {}
};

const sendInvite = async (e) => {
e.preventDefault();
setMessage(''); setError('');
try {
await api.post('/admin/invite', inviteForm);
setMessage('Invite sent successfully!');
setInviteForm({ email: '', phone: '', name: '' });
loadData();
} catch (err) {
setError(err.response?.data?.error || 'Failed to send invite');
}
};

const addNavItem = async (e) => {
e.preventDefault();
setMessage(''); setError('');
try {
await api.post('/admin/nav', navForm);
setMessage('Nav item added!');
setNavForm({ parent: 'recipes', label: '', search_value: '' });
loadData();
} catch (err) {
setError(err.response?.data?.error || 'Failed to add nav item');
}
};

const deleteNavItem = async (id) => {
if (!window.confirm('Delete this nav item?')) return;
try {
await api.delete(`/admin/nav/${id}`);
loadData();
} catch {}
};

const toggleUser = async (id) => {
try {
await api.patch(`/admin/users/${id}/toggle`);
loadData();
} catch {}
};

return (
<div className="admin-page container">
<div className="admin-page__header">
<h1> Admin Dashboard</h1>
<p>RecipeSense management panel</p>
</div>

{/* Tabs */}
<div className="admin-tabs">
{['overview', 'users', 'invites', 'navigation'].map(t => (
<button
key={t}
className={`admin-tab ${tab === t ? 'active' : ''}`}
onClick={() => setTab(t)}
>
{t.charAt(0).toUpperCase() + t.slice(1)}
</button>
))}
</div>

{message && <div className="account-success" style={{marginBottom: '1rem'}}>{message}</div>}
{error && <div className="account-error" style={{marginBottom: '1rem'}}>{error}</div>}

{/* Overview */}
{tab === 'overview' && stats && (
<div className="admin-stats">
<div className="admin-stat-card">
<span className="admin-stat-value">{stats.total_users}</span>
<span className="admin-stat-label">Total Users</span>
</div>
<div className="admin-stat-card">
<span className="admin-stat-value">{stats.total_recipes}</span>
<span className="admin-stat-label">Unique Recipes</span>
</div>
<div className="admin-stat-card">
<span className="admin-stat-value">{stats.total_user_recipes}</span>
<span className="admin-stat-label">User Collections</span>
</div>
<div className="admin-stat-card">
<span className="admin-stat-value">{stats.pending_invites}</span>
<span className="admin-stat-label">Pending Invites</span>
</div>
</div>
)}

{/* Users */}
{tab === 'users' && (
<div className="admin-card">
<h2>Users</h2>
<table className="admin-table">
<thead>
<tr>
<th>Name</th>
<th>Phone</th>
<th>Email</th>
<th>Role</th>
<th>Recipes</th>
<th>Status</th>
<th>Actions</th>
</tr>
</thead>
<tbody>
{users.map(u => (
<tr key={u.id}>
<td>{u.name}</td>
<td>{u.phone}</td>
<td>{u.email}</td>
<td><span className={`admin-badge admin-badge--${u.role}`}>{u.role}</span></td>
<td>{u.recipe_count}</td>
<td><span className={`admin-badge admin-badge--${u.is_active ? 'active' : 'inactive'}`}>
{u.is_active ? 'Active' : 'Disabled'}
</span></td>
<td>
{u.phone !== '602-716-1121' && (
<button className="btn btn-ghost" onClick={() => toggleUser(u.id)}>
{u.is_active ? 'Disable' : 'Enable'}
</button>
)}
</td>
</tr>
))}
</tbody>
</table>
</div>
)}

{/* Invites */}
{tab === 'invites' && (
<div className="admin-grid">
<div className="admin-card">
<h2>Send Invite</h2>
<form onSubmit={sendInvite} className="account-form">
<div className="form-group">
<label>Name (optional)</label>
<input type="text" value={inviteForm.name}
onChange={e => setInviteForm({...inviteForm, name: e.target.value})}
placeholder="Friend's name" />
</div>
<div className="form-group">
<label>Email Address</label>
<input type="email" value={inviteForm.email}
onChange={e => setInviteForm({...inviteForm, email: e.target.value})}
placeholder="friend@example.com" />
</div>
<div className="form-group">
<label>Phone (optional)</label>
<input type="tel" value={inviteForm.phone}
onChange={e => setInviteForm({...inviteForm, phone: e.target.value})}
placeholder="602-555-1234" />
</div>
<button type="submit" className="btn btn-primary">Send Invite</button>
</form>
</div>

<div className="admin-card">
<h2>Sent Invitations</h2>
<div className="admin-invites">
{invitations.length === 0 ? (
<p style={{color: 'var(--color-text-muted)'}}>No invitations sent yet.</p>
) : invitations.map(inv => (
<div key={inv.id} className="admin-invite-item">
<div>
<strong>{inv.email || inv.phone}</strong>
<span className={`admin-badge admin-badge--${inv.status}`}>{inv.status}</span>
</div>
<small>{new Date(inv.created_at).toLocaleDateString()}</small>
</div>
))}
</div>
</div>
</div>
)}

{/* Navigation */}
{tab === 'navigation' && (
<div className="admin-grid">
<div className="admin-card">
<h2>Add Nav Item</h2>
<form onSubmit={addNavItem} className="account-form">
<div className="form-group">
<label>Section</label>
<select value={navForm.parent} onChange={e => setNavForm({...navForm, parent: e.target.value})}>
<option value="recipes">Recipes</option>
<option value="ingredients">Ingredients</option>
</select>
</div>
<div className="form-group">
<label>Label</label>
<input type="text" value={navForm.label}
onChange={e => setNavForm({...navForm, label: e.target.value})}
placeholder="e.g. Air Fryer" required />
</div>
<div className="form-group">
<label>Search Value</label>
<input type="text" value={navForm.search_value}
onChange={e => setNavForm({...navForm, search_value: e.target.value})}
placeholder="e.g. air-fryer" required />
</div>
<button type="submit" className="btn btn-primary">Add Item</button>
</form>
</div>

<div className="admin-card">
<h2>Current Nav Items</h2>
<div className="admin-nav-list">
{['recipes', 'ingredients'].map(section => (
<div key={section} className="admin-nav-section">
<h4>{section.charAt(0).toUpperCase() + section.slice(1)}</h4>
{navItems.filter(n => n.parent === section).map(item => (
<div key={item.id} className="admin-nav-item">
<span>{item.label}</span>
<span className="admin-nav-value">{item.search_value}</span>
<button className="btn btn-ghost" style={{color: 'var(--color-error)'}}
onClick={() => deleteNavItem(item.id)}></button>
</div>
))}
</div>
))}
</div>
</div>
</div>
)}
</div>
);
}
